<template>
  <div class="booking-detail-public-page">
    <!-- Top Header Bar -->
    <header class="booking-header">
      <div class="container header-container">
        <div class="brand-logo" @click="handleGoHome">
          <div class="logo-icon">
            <i class="fas fa-ticket-alt" />
          </div>
          <div class="brand-text">
            <h1 class="brand-title">TDM BOOKING</h1>
            <span class="brand-subtitle">Chi tiết điểm đến & Đặt vé</span>
          </div>
        </div>

        <nav class="header-nav">
          <el-button
            type="text"
            icon="el-icon-back"
            class="nav-link"
            @click="handleGoHome"
          >
            Quay lại danh sách
          </el-button>
          <el-button
            type="primary"
            round
            icon="el-icon-tickets"
            class="my-orders-btn"
            @click="handleGoMyOrders"
          >
            Đơn hàng của tôi
          </el-button>
        </nav>
      </div>
    </header>

    <!-- Embedded Place Detail Info (Kept at the Top of Page) -->
    <div class="place-detail-wrapper">
      <PlaceDetailPublic ref="placeDetail" />
    </div>

    <!-- Booking Action & Multi-Step Checkout Section -->
    <section id="booking-section" class="container booking-section py-5">
      <el-card class="booking-checkout-card" shadow="always">
        <div slot="header" class="card-header-flex py-2">
          <h3 class="m-0 font-weight-bold title">
            <i class="fas fa-shopping-cart text-primary mr-2" /> ĐẶT VÉ TRẢI NGHIỆM
          </h3>
          <div class="header-badges">
            <el-tag v-if="product" type="success" effect="dark" class="mr-2">
              MÃ PRODUCT: {{ product.code || product.id }}
            </el-tag>
            <el-tag v-if="currentOrder && currentOrder.status === 'PENDING'" type="warning" effect="dark">
              ⌛ ĐANG TẠM GIỮ (PENDING): {{ countdownText }}
            </el-tag>
          </div>
        </div>

        <!-- Date Selection Banner (Top of ĐẶT VÉ TRẢI NGHIỆM section) -->
        <div class="date-selection-box mb-4 p-3 bg-light rounded border">
          <el-row :gutter="16" align="middle" type="flex" class="flex-wrap">
            <el-col :xs="24" :sm="12" :md="8">
              <div class="form-label font-weight-bold text-dark mb-2 d-block">
                <i class="far fa-calendar-alt text-primary mr-1" /> CHỌN NGÀY THAM QUAN:
              </div>
              <el-date-picker
                v-model="bookingDate"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="Chọn ngày tham quan"
                style="width: 100%;"
                :picker-options="pickerOptions"
                @change="handleBookingDateChange"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="16" class="mt-2 mt-sm-0">
              <span class="text-muted font-size-xs">
                💡 <em>Vui lòng chọn ngày tham quan để kiểm tra chính xác sức chứa vé trống, bảng giá ưu đãi và chọn số lượng đặt vé.</em>
              </span>
            </el-col>
          </el-row>
        </div>

        <!-- Pending Order Expiry Alert Banner -->
        <el-alert
          v-if="isCheckingOut && currentOrder && currentOrder.status === 'PENDING'"
          type="warning"
          show-icon
          :closable="false"
          class="mb-4"
        >
          <template slot="title">
            <strong>Đơn hàng đang ở trạng thái TẠM GIỮ (PENDING).</strong>
            Vé của bạn sẽ được giữ trong <strong>15 phút</strong>. Thời gian còn lại:
            <span class="countdown-badge">{{ countdownText }}</span>
          </template>
        </el-alert>

        <!-- Capacity Status Display -->
        <div class="capacity-status-card mb-4">
          <div class="capacity-header d-flex justify-content-between align-items-center mb-2">
            <h4 class="m-0 font-weight-bold text-dark font-size-md">
              <i class="fas fa-chart-pie text-info mr-1" /> Tình trạng sức chứa ngày {{ formatDate(bookingDate) }}:
            </h4>
            <el-button icon="el-icon-refresh" size="mini" circle title="Làm mới tình trạng vé" @click="fetchCapacitiesAndOrders" />
          </div>

          <el-row :gutter="12" class="capacity-tags-row">
            <el-col :xs="12" :sm="6">
              <div class="cap-box cap-available">
                <span class="cap-label">Vé trống (Còn lại)</span>
                <strong class="cap-val text-primary">{{ availableCount }} vé</strong>
              </div>
            </el-col>
            <el-col :xs="12" :sm="6">
              <div class="cap-box cap-booked">
                <span class="cap-label">Vé đã đặt</span>
                <strong class="cap-val text-success">{{ bookedCount }} vé</strong>
              </div>
            </el-col>
            <el-col :xs="12" :sm="6">
              <div class="cap-box cap-pending">
                <span class="cap-label">Vé đang chờ (Holding)</span>
                <strong class="cap-val text-warning">{{ pendingCount }} vé</strong>
              </div>
            </el-col>
            <el-col :xs="12" :sm="6">
              <div class="cap-box cap-total">
                <span class="cap-label">Tổng sức chứa</span>
                <strong class="cap-val text-secondary">{{ totalCapacity }} vé</strong>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- Initial Pricing & Selection State -->
        <div v-if="!isCheckingOut" class="pricing-selection-wrap">
          <el-row :gutter="24">
            <!-- Left side: Price Calculations -->
            <el-col :xs="24" :md="14">
              <h4 class="sub-title"><i class="fas fa-calculator mr-1" /> Bảng tính giá chi tiết ngày {{ formatDate(bookingDate) }}</h4>
              <div class="price-table-box">
                <div class="price-row">
                  <span>Giá cơ bản (Base price):</span>
                  <strong>{{ formatCurrency(basePrice) }}</strong>
                </div>
                <div v-if="markupPercent > 0" class="price-row text-success">
                  <span>Phụ thu / Thêm (+{{ markupPercent }}%):</span>
                  <span>+{{ formatCurrency(markupAmount) }}</span>
                </div>
                <div v-if="promotionPercent > 0" class="price-row text-danger">
                  <span>Khuyến mãi (-{{ promotionPercent }}%):</span>
                  <span>-{{ formatCurrency(promotionAmount) }}</span>
                </div>
                <div class="price-divider" />
                <div class="price-row">
                  <span>Giá trước thuế:</span>
                  <strong>{{ formatCurrency(subtotalPrice) }}</strong>
                </div>
                <div v-if="taxPercent > 0" class="price-row text-warning">
                  <span>Thuế GTGT (+{{ taxPercent }}%):</span>
                  <span>+{{ formatCurrency(taxAmount) }}</span>
                </div>
                <div class="price-divider" />
                <div class="price-row total-unit-row">
                  <span>Giá 1 vé thành tiền:</span>
                  <span class="unit-price">{{ formatCurrency(finalUnitPrice) }}</span>
                </div>
              </div>
            </el-col>

            <!-- Right side: Quantity & Continue -->
            <el-col :xs="24" :md="10" class="quantity-col">
              <div class="booking-box">
                <div class="form-group mb-4">
                  <label class="form-label">Số lượng vé muốn đặt:</label>
                  <el-input-number
                    v-model="quantity"
                    :min="availableCount > 0 ? 1 : 0"
                    :max="availableCount > 0 ? availableCount : 0"
                    :disabled="availableCount <= 0"
                    size="medium"
                    style="width: 100%;"
                    @change="handleQuantityChange"
                  />
                  <span v-if="availableCount <= 0" class="text-danger font-size-xs mt-1 display-block">
                    Rất tiếc! Ngày {{ formatDate(bookingDate) }} không nằm trong danh sách slot mở bán (Hết vé / Không có suất).
                  </span>
                  <span v-else class="text-muted font-size-xs mt-1 display-block">
                    Tối đa có thể đặt ngày {{ formatDate(bookingDate) }}: {{ availableCount }} vé
                  </span>
                </div>

                <div class="total-summary-box mb-4">
                  <div class="total-label">TỔNG TIỀN TẠM TÍNH:</div>
                  <div class="total-price">{{ formatCurrency(totalPrice) }}</div>
                </div>

                <el-button
                  type="warning"
                  size="large"
                  icon="el-icon-right"
                  class="btn-continue"
                  :disabled="availableCount <= 0 || quantity <= 0"
                  @click="handleStartCheckout"
                >
                  TIẾP TỤC ĐẶT VÉ
                </el-button>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- Multi-Step Checkout Flow -->
        <div v-else class="checkout-steps-wrap">
          <el-steps :active="activeStep" finish-status="success" align-center class="mb-5">
            <el-step title="Trả lời câu hỏi" icon="el-icon-edit-outline" />
            <el-step title="Xác nhận booking" icon="el-icon-document-checked" />
            <el-step title="Thanh toán" icon="el-icon-bank-card" />
            <el-step title="Hoàn tất" icon="el-icon-circle-check" />
          </el-steps>

          <!-- Step 1: Form Questions -->
          <div v-if="activeStep === 0" class="step-content">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <h4 class="step-title m-0">
                <i class="fas fa-user-edit text-primary mr-2" /> 1. Thông tin từng vé (Tổng {{ quantity }} vé)
              </h4>
              <div class="apply-all-checkbox my-2">
                <el-checkbox v-model="applyToAll" @change="handleApplyToAllChange">
                  <i class="fas fa-copy text-primary mr-1" />
                  <strong>Áp dụng thông tin Vé 1 cho tất cả</strong>
                </el-checkbox>
              </div>
            </div>

            <el-tabs v-model="activeTicketTab" type="border-card" class="ticket-tabs mb-3">
              <el-tab-pane v-for="(ticket, idx) in ticketsAnswers" :key="idx" :name="String(idx)">
                <span slot="label"><i class="fas fa-ticket-alt mr-1" /> Vé {{ idx + 1 }}</span>
                <el-form :ref="`ticketForm_${idx}`" :model="ticket" label-position="top">
                  <el-row :gutter="16">
                    <el-col v-for="q in questionList" :key="q.id || q.key" :xs="24" :sm="12">
                      <el-form-item :label="getQuestionLabel(q)" :prop="`custom_${q.key}`" :rules="q.required ? [{ required: true, message: `Vui lòng điền ${getQuestionLabel(q)}`, trigger: 'blur' }] : []">
                        <el-input v-if="!q.answer_type || q.answer_type === 'text'" v-model="ticket[`custom_${q.key}`]" :placeholder="`Nhập ${getQuestionLabel(q)}`" @input="onTicketInput(idx)" />
                        <el-input-number v-else-if="q.answer_type === 'number'" v-model="ticket[`custom_${q.key}`]" style="width: 100%;" @change="onTicketInput(idx)" />
                        <el-switch v-else-if="q.answer_type === 'boolean'" v-model="ticket[`custom_${q.key}`]" active-text="Có" inactive-text="Không" @change="onTicketInput(idx)" />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </el-tab-pane>
            </el-tabs>

            <div class="step-actions mt-4 text-right">
              <el-button plain @click="handleCancelCheckout">Hủy bỏ</el-button>
              <el-button type="primary" icon="el-icon-right" @click="handleNextToConfirm">Tiếp tục đến Xác nhận</el-button>
            </div>
          </div>

          <!-- Step 2: Confirmation -->
          <div v-else-if="activeStep === 1" class="step-content">
            <h4 class="step-title"><i class="fas fa-clipboard-check text-primary mr-2" /> 2. Xác nhận thông tin đơn hàng</h4>
            <div class="confirm-summary-grid">
              <el-row :gutter="20">
                <el-col :xs="24" :md="14">
                  <div class="summary-box">
                    <h5 class="summary-box-title">Thông tin các vé đã đăng ký ({{ quantity }} vé)</h5>
                    <p><strong>Ngày tham quan:</strong> {{ formatDate(bookingDate) }}</p>
                    <div v-for="(t, idx) in ticketsAnswers" :key="idx" class="ticket-confirm-item mt-3 pt-3 border-top">
                      <h6 class="font-weight-bold text-primary"><i class="fas fa-ticket-alt mr-1" /> VÉ {{ idx + 1 }}</h6>
                      <div v-for="q in questionList" :key="q.id || q.key" class="font-size-sm text-dark mb-1">
                        • <strong>{{ getQuestionLabel(q) }}:</strong> {{ t[`custom_${q.key}`] !== undefined ? t[`custom_${q.key}`] : 'N/A' }}
                      </div>
                    </div>
                  </div>
                </el-col>

                <!-- Pricing Breakdown -->
                <el-col :xs="24" :md="10">
                  <div class="summary-box highlight-box">
                    <h5 class="summary-box-title">Chi tiết thanh toán</h5>
                    <div class="summary-row">
                      <span>Địa danh:</span>
                      <strong>{{ (placeDetail && placeDetail.name) || 'Địa danh' }}</strong>
                    </div>
                    <div class="summary-row">
                      <span>Đơn giá / 1 vé:</span>
                      <strong>{{ formatCurrency(finalUnitPrice) }}</strong>
                    </div>
                    <div class="summary-row">
                      <span>Số lượng:</span>
                      <strong>x {{ quantity }} vé</strong>
                    </div>
                    <div class="summary-divider" />
                    <div class="summary-row total-row">
                      <span>Tổng tiền cần thanh toán:</span>
                      <strong class="text-danger">{{ formatCurrency(totalPrice) }}</strong>
                    </div>
                  </div>
                </el-col>
              </el-row>
            </div>

            <div class="step-actions mt-4 d-flex justify-content-between">
              <el-button icon="el-icon-back" @click="activeStep = 0">Quay lại chỉnh sửa</el-button>
              <el-button type="primary" icon="el-icon-right" @click="handleProceedToPayment">
                Xác nhận & Chuyển sang Thanh toán
              </el-button>
            </div>
          </div>

          <!-- Step 3: Màn hình thanh toán fake (4.3) -->
          <div v-else-if="activeStep === 2" class="step-content">
            <h4 class="step-title"><i class="fas fa-credit-card text-primary mr-2" /> 3. Giả lập cổng thanh toán trực tuyến</h4>

            <div class="payment-methods-wrap">
              <h5 class="mb-3">Chọn phương thức thanh toán:</h5>
              <el-radio-group v-model="paymentMethod" size="medium" class="payment-radio-group">
                <el-radio-button label="momo">
                  <i class="fas fa-wallet mr-1" /> Ví Điện Tử MoMo
                </el-radio-button>
                <el-radio-button label="vnpay">
                  <i class="fas fa-qrcode mr-1" /> VNPAY QR
                </el-radio-button>
                <el-radio-button label="card">
                  <i class="far fa-credit-card mr-1" /> Thẻ Quốc Tế / ATM
                </el-radio-button>
                <el-radio-button label="bank">
                  <i class="fas fa-university mr-1" /> Chuyển Khoản Ngân Hàng
                </el-radio-button>
              </el-radio-group>

              <!-- Payment Simulated Details -->
              <div class="simulated-gateway-box mt-4">
                <div v-if="paymentMethod === 'momo' || paymentMethod === 'vnpay'" class="qr-pay-box text-center">
                  <div class="qr-placeholder">
                    <i class="fas fa-qrcode qr-icon" />
                    <p class="mt-2 text-muted">Mã QR Thanh Toán Giả Lập</p>
                  </div>
                  <p class="font-weight-bold text-dark mt-2">
                    Số tiền cần thanh toán: <span class="text-danger">{{ formatCurrency(totalPrice) }}</span>
                  </p>
                  <span class="text-muted font-size-sm">Quét mã QR bằng ứng dụng ngân hàng hoặc ví điện tử</span>
                </div>

                <div v-else-if="paymentMethod === 'card'" class="card-pay-box">
                  <el-form label-position="top" size="small">
                    <el-form-item label="Số thẻ ATM / Visa / Mastercard">
                      <el-input placeholder="4123 **** **** 8888" value="4123 4567 8901 8888" />
                    </el-form-item>
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item label="Hạn dùng">
                          <el-input placeholder="MM/YY" value="12/28" />
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="Mã CVC/CVV">
                          <el-input placeholder="123" value="999" />
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </el-form>
                </div>

                <div v-else class="bank-pay-box">
                  <p><strong>Ngân hàng:</strong> Vietcombank - Chi nhánh TP.HCM</p>
                  <p><strong>Số tài khoản:</strong> 99887766554433</p>
                  <p><strong>Chủ tài khoản:</strong> CONG TY TDM BOOKING</p>
                  <p><strong>Nội dung chuyển khoản:</strong> TDM BOOKING {{ (ticketsAnswers[0] && ticketsAnswers[0].phone) || '' }}</p>
                </div>
              </div>
            </div>

            <div class="step-actions mt-4 text-right">
              <el-button plain icon="el-icon-back" @click="activeStep = 1">Quay lại</el-button>
              <el-button
                type="warning"
                size="medium"
                icon="el-icon-success"
                :loading="submittingPayment"
                @click="handleProcessPayment"
              >
                Xác nhận đã thanh toán
              </el-button>
            </div>
          </div>

          <!-- Step 4: Thanh toán thành công & Lưu đơn hàng (4.4) -->
          <div v-else-if="activeStep === 3" class="step-content text-center py-4">
            <div class="success-icon-wrap mb-3">
              <i class="fas fa-check-circle success-check-icon" />
            </div>

            <h3 class="text-success font-weight-bold mb-2">THANH TOÁN THÀNH CÔNG!</h3>
            <p class="text-muted mb-4">Đơn hàng của bạn đã được ghi nhận vào hệ thống thành công.</p>

            <div v-if="completedOrder" class="order-receipt-card mb-4 text-left">
              <div class="receipt-header">
                <strong>MÃ ĐƠN HÀNG: {{ completedOrder.code }}</strong>
                <el-tag type="success" size="small">ĐÃ THANH TOÁN</el-tag>
              </div>
              <div class="receipt-body">
                <p><strong>Địa điểm:</strong> {{ completedOrder.place_name }}</p>
                <p><strong>Người đại diện:</strong> {{ completedOrder.customer.full_name }} ({{ completedOrder.customer.phone }})</p>
                <p><strong>Ngày đi:</strong> {{ formatDate(completedOrder.booking_date) }}</p>
                <p><strong>Số lượng:</strong> {{ completedOrder.quantity }} vé</p>
                <p><strong>Tổng thanh toán:</strong> <strong class="text-danger">{{ formatCurrency(completedOrder.total_price) }}</strong></p>
              </div>
            </div>

            <div class="receipt-actions">
              <el-button type="primary" icon="el-icon-tickets" @click="handleGoMyOrders">
                Xem danh sách đơn hàng của tôi
              </el-button>
              <el-button plain icon="el-icon-house" @click="handleGoHome">
                Trở về trang chủ Booking
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </section>
  </div>
</template>

<script>
import PlaceDetailPublic from '@/public-views/place/detail.vue'
import { getPlaceById } from '@/services/place'
import { getProducts, getPrices, getQuestions, createQuestion, getCapacities, createCapacity } from '@/services/product'

export default {
  name: 'BookingDetailPublic',
  components: {
    PlaceDetailPublic
  },
  data() {
    return {
      loading: true,
      placeId: '',
      placeDetail: null,
      product: null,
      priceList: [],
      questionList: [],
      capacityList: [],

      // Capacities counters
      totalCapacity: 0,
      bookedCount: 0,
      pendingCount: 0,
      availableCount: 0,

      // Selection state
      bookingDate: new Date().toISOString().substring(0, 10),
      quantity: 1,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        }
      },

      // Step & Checkout state
      isCheckingOut: false,
      activeStep: 0,
      activeTicketTab: '0',
      applyToAll: false,
      paymentMethod: 'momo',
      submittingPayment: false,
      completedOrder: null,
      currentOrder: null,

      // Countdown Timer
      countdownInterval: null,
      countdownSeconds: 0,

      // Form state for multiple tickets
      ticketsAnswers: []
    }
  },
  computed: {
    countdownText() {
      if (this.countdownSeconds <= 0) return '00:00'
      const m = Math.floor(this.countdownSeconds / 60)
      const s = this.countdownSeconds % 60
      return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`
    },
    basePrice() {
      return (this.product && this.product.base_price !== undefined) ? Number(this.product.base_price) : 0
    },
    markupPercent() {
      const markups = this.priceList.filter((p) => p.type === 'markup')
      return markups.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    },
    promotionPercent() {
      const promotions = this.priceList.filter((p) => p.type === 'promotion')
      return promotions.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    },
    taxPercent() {
      const taxes = this.priceList.filter((p) => p.type === 'tax')
      return taxes.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    },
    markupAmount() {
      return this.basePrice * (this.markupPercent / 100)
    },
    promotionAmount() {
      return this.basePrice * (this.promotionPercent / 100)
    },
    subtotalPrice() {
      const sub = this.basePrice + this.markupAmount - this.promotionAmount
      return sub > 0 ? sub : 0
    },
    taxAmount() {
      return this.subtotalPrice * (this.taxPercent / 100)
    },
    finalUnitPrice() {
      return this.subtotalPrice + this.taxAmount
    },
    totalPrice() {
      return this.finalUnitPrice * this.quantity
    }
  },
  mounted() {
    this.placeId = this.$route.params.id
    this.initData()
    this.syncTicketsAnswers()
  },
  beforeDestroy() {
    this.stopCountdownTimer()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      return dateStr
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return '0 VNĐ'
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val)
    },
    handleGoHome() {
      this.$router.push('/booking')
    },
    handleGoMyOrders() {
      this.$router.push('/booking/me')
    },
    getQuestionLabel(q) {
      if (!q) return ''
      if (typeof q.label === 'string') return q.label
      if (q.label) return q.label.vi || q.label.en || q.key
      return q.key || ''
    },

    getQuestionLabelsMap() {
      const map = {}
      if (this.questionList && this.questionList.length) {
        this.questionList.forEach((q) => {
          map[q.key] = this.getQuestionLabel(q)
        })
      }
      return map
    },

    // ----------------------------------------------------
    // Auto-Expire Pending Orders (15 minutes threshold)
    // ----------------------------------------------------
    checkAndExpirePendingOrders() {
      try {
        const stored = localStorage.getItem('booking_orders_fake_db')
        if (!stored) return
        const orders = JSON.parse(stored)
        let changed = false
        const now = Date.now()

        orders.forEach((o) => {
          if (o.status === 'PENDING') {
            const expireTime = o.expires_at || (o.created_at_timestamp ? o.created_at_timestamp + 15 * 60 * 1000 : 0)
            if (expireTime && now > expireTime) {
              o.status = 'CANCELLED'
              changed = true
            }
          }
        })

        if (changed) {
          localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
        }
      } catch (e) {
        console.error('Error checking pending order expiration:', e)
      }
    },

    // Calculate Available / Booked / Pending Capacity Counters
    fetchCapacitiesAndOrders() {
      this.checkAndExpirePendingOrders()

      const prodId = this.product ? (this.product.id || this.product._id) : null
      let total = 0
      let booked = 0
      let pending = 0

      if (this.capacityList && this.capacityList.length) {
        const capForDate = this.capacityList.find((c) => {
          const slotDateStr = c.slot_date ? c.slot_date.substring(0, 10) : (c.date ? String(c.date).substring(0, 10) : '')
          if (slotDateStr && slotDateStr === String(this.bookingDate)) return true
          if (c.start_date && c.end_date) {
            const start = c.start_date.substring(0, 10)
            const end = c.end_date.substring(0, 10)
            return this.bookingDate >= start && this.bookingDate <= end
          }
          if (c.start_date && c.start_date.substring(0, 10) === String(this.bookingDate)) return true
          return false
        })

        if (capForDate) {
          total = Number(capForDate.total_capacity || capForDate.capacity || capForDate.max_capacity) || 0
        } else {
          total = 0
        }
      }

      // Read orders to sum booked & pending
      try {
        const stored = localStorage.getItem('booking_orders_fake_db')
        if (stored) {
          const orders = JSON.parse(stored)
          orders.forEach((o) => {
            if (prodId && String(o.product_id) === String(prodId) && String(o.booking_date) === String(this.bookingDate)) {
              if (o.status === 'PAID') {
                booked += Number(o.quantity) || 0
              } else if (o.status === 'PENDING') {
                pending += Number(o.quantity) || 0
              }
            }
          })
        }
      } catch (e) {
        console.error(e)
      }

      this.totalCapacity = total
      this.bookedCount = booked
      this.pendingCount = pending
      this.availableCount = Math.max(0, total - booked - pending)
    },

    syncTicketsAnswers() {
      const currentLen = this.ticketsAnswers.length
      const targetLen = this.quantity || 1

      if (currentLen < targetLen) {
        for (let i = currentLen; i < targetLen; i++) {
          const newTicket = {
            full_name: '',
            phone: '',
            email: '',
            note: ''
          }
          this.ticketsAnswers.push(newTicket)
        }
      } else if (currentLen > targetLen) {
        this.ticketsAnswers = this.ticketsAnswers.slice(0, targetLen)
      }

      if (this.applyToAll && this.ticketsAnswers.length > 1) {
        this.copyTicket1ToAll()
      }
    },

    handleBookingDateChange() {
      this.fetchCapacitiesAndOrders()
      if (this.availableCount <= 0) {
        this.quantity = 0
      } else if (this.quantity <= 0) {
        this.quantity = 1
      } else if (this.quantity > this.availableCount) {
        this.quantity = this.availableCount
      }
      this.syncTicketsAnswers()
    },

    handleQuantityChange() {
      this.syncTicketsAnswers()
    },

    onTicketInput(idx) {
      if (idx === 0 && this.applyToAll) {
        this.copyTicket1ToAll()
      }
      this.savePendingOrderState()
    },

    handleApplyToAllChange(val) {
      if (val) {
        this.copyTicket1ToAll()
      }
      this.savePendingOrderState()
    },

    copyTicket1ToAll() {
      if (!this.ticketsAnswers.length) return
      const ticket1 = JSON.parse(JSON.stringify(this.ticketsAnswers[0]))
      for (let i = 1; i < this.ticketsAnswers.length; i++) {
        this.$set(this.ticketsAnswers, i, JSON.parse(JSON.stringify(ticket1)))
      }
    },

    async initData() {
      this.loading = true
      try {
        const placeData = await getPlaceById({ id: this.placeId }).catch(() => null)
        this.placeDetail = placeData

        let productId = null
        try {
          const stored = localStorage.getItem('shared_booking_products')
          if (stored) {
            const mappings = JSON.parse(stored)
            const found = mappings.find((item) => String(item.detail_id) === String(this.placeId))
            if (found) productId = found.product_id
          }
        } catch (e) {
          console.error(e)
        }

        const prodRes = await getProducts()
        const products = prodRes.data || prodRes || []
        if (!productId) {
          const matchedProd = products.find((p) => String(p.reference_id) === String(this.placeId))
          if (matchedProd) productId = matchedProd.id || matchedProd._id
        }

        if (productId) {
          this.product = products.find((p) => String(p.id) === String(productId) || String(p._id) === String(productId))

          const priceRes = await getPrices({ applies_to_value: productId }).catch(() => ({ data: [] }))
          const pricesData = priceRes.data || priceRes || []
          this.priceList = Array.isArray(pricesData) ? pricesData : []

          const questionRes = await getQuestions(productId).catch(() => ({ data: [] }))
          const qData = questionRes.data || questionRes || []
          let questions = Array.isArray(qData) ? qData : []

          if (!questions.length) {
            const defaultQuestions = [
              { key: 'cccd', label: { vi: 'Số CCCD/CMND', en: 'ID Card / Passport' }, answer_type: 'text', required: true },
              { key: 'dob', label: { vi: 'Ngày sinh', en: 'Date of birth' }, answer_type: 'text', required: false },
              { key: 'dietary', label: { vi: 'Yêu cầu ăn uống', en: 'Dietary requirements' }, answer_type: 'text', required: false }
            ]
            for (const dq of defaultQuestions) {
              await createQuestion(productId, dq).catch(() => null)
            }
            const reFetch = await getQuestions(productId).catch(() => ({ data: [] }))
            questions = reFetch.data || reFetch || []
          }
          this.questionList = questions

          const capRes = await getCapacities(productId).catch(() => ({ data: [] }))
          const capData = capRes.data || capRes || []
          let capList = Array.isArray(capData) ? capData : []

          if (!capList.length) {
            const defaultSlot = {
              slot_date: new Date(this.bookingDate).toISOString(),
              total_capacity: 50
            }
            await createCapacity(productId, defaultSlot).catch(() => null)
            const reCap = await getCapacities(productId).catch(() => ({ data: [] }))
            capList = reCap.data || reCap || []
          }
          this.capacityList = capList
        }

        this.fetchCapacitiesAndOrders()

        // Check if query order_id exists to resume booking
        if (this.$route.query.order_id) {
          this.resumePendingOrder(this.$route.query.order_id)
        }
      } catch (err) {
        console.error('Error loading detail data:', err)
      } finally {
        this.loading = false
      }
    },

    resumePendingOrder(orderId) {
      try {
        const stored = localStorage.getItem('booking_orders_fake_db')
        if (!stored) return
        const orders = JSON.parse(stored)
        const found = orders.find((o) => String(o.id) === String(orderId))

        if (found && found.status === 'PENDING') {
          const now = Date.now()
          const expireTime = found.expires_at || (found.created_at_timestamp + 15 * 60 * 1000)
          if (now < expireTime) {
            this.currentOrder = found
            this.bookingDate = found.booking_date
            this.quantity = found.quantity
            this.ticketsAnswers = found.tickets || [found.customer]
            this.isCheckingOut = true
            this.activeStep = found.step || 0

            const remainingMs = expireTime - now
            this.startCountdownTimer(Math.floor(remainingMs / 1000))
          } else {
            found.status = 'CANCELLED'
            localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
            this.$message.warning('Đơn hàng này đã hết thời hạn tạm giữ (15 phút) và bị hủy!')
          }
        }
      } catch (e) {
        console.error(e)
      }
    },

    // Create / Save PENDING Order immediately when starting checkout
    handleStartCheckout() {
      if (this.availableCount <= 0) {
        this.$message.error('Rất tiếc! Suất vé này đã hết vé trống.')
        return
      }

      if (this.quantity > this.availableCount) {
        this.$message.error(`Số lượng vé bạn chọn (${this.quantity}) vượt quá số vé trống còn lại (${this.availableCount})!`)
        return
      }

      this.syncTicketsAnswers()

      // Create PENDING order
      const orderId = `BOOKING-${Date.now().toString().substring(5)}`
      const now = Date.now()
      const expiresAt = now + 15 * 60 * 1000 // 15 minutes hold

      const pendingOrder = {
        id: orderId,
        code: orderId,
        place_id: this.placeId,
        place_name: (this.placeDetail && this.placeDetail.name) || 'Địa danh',
        place_image: (this.placeDetail && (this.placeDetail.banner || this.placeDetail.thumbnail)) || 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?auto=format&fit=crop&w=800&q=80',
        product_id: this.product ? (this.product.id || this.product._id) : '',
        booking_date: this.bookingDate,
        quantity: this.quantity,
        base_price: this.basePrice,
        unit_price: this.finalUnitPrice,
        total_price: this.totalPrice,
        price_breakdown: {
          markup_percent: this.markupPercent,
          promotion_percent: this.promotionPercent,
          tax_percent: this.taxPercent
        },
        customer: { ...this.ticketsAnswers[0] },
        tickets: JSON.parse(JSON.stringify(this.ticketsAnswers)),
        question_labels: this.getQuestionLabelsMap(),
        payment_method: this.paymentMethod,
        status: 'PENDING',
        created_at_timestamp: now,
        expires_at: expiresAt,
        step: 0,
        created_at: new Date().toLocaleDateString('vi-VN') + ' ' + new Date().toLocaleTimeString('vi-VN')
      }

      try {
        let orders = []
        const storedOrders = localStorage.getItem('booking_orders_fake_db')
        if (storedOrders) orders = JSON.parse(storedOrders)
        orders.unshift(pendingOrder)
        localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
      } catch (e) {
        console.error('Error saving pending order to localStorage:', e)
      }

      this.currentOrder = pendingOrder
      this.isCheckingOut = true
      this.activeStep = 0
      this.activeTicketTab = '0'

      this.startCountdownTimer(15 * 60)
      this.fetchCapacitiesAndOrders()

      this.$nextTick(() => {
        const el = document.getElementById('booking-section')
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      })
    },

    savePendingOrderState() {
      if (!this.currentOrder || this.currentOrder.status !== 'PENDING') return
      try {
        const stored = localStorage.getItem('booking_orders_fake_db')
        if (!stored) return
        const orders = JSON.parse(stored)
        const idx = orders.findIndex((o) => String(o.id) === String(this.currentOrder.id))

        if (idx !== -1) {
          orders[idx].tickets = JSON.parse(JSON.stringify(this.ticketsAnswers))
          orders[idx].customer = { ...this.ticketsAnswers[0] }
          orders[idx].step = this.activeStep
          orders[idx].payment_method = this.paymentMethod
          localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
          this.currentOrder = orders[idx]
        }
      } catch (e) {
        console.error(e)
      }
    },

    startCountdownTimer(seconds) {
      this.stopCountdownTimer()
      this.countdownSeconds = seconds

      this.countdownInterval = setInterval(() => {
        this.countdownSeconds--
        if (this.countdownSeconds <= 0) {
          this.stopCountdownTimer()
          this.handlePendingOrderExpired()
        }
      }, 1000)
    },

    stopCountdownTimer() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    handlePendingOrderExpired() {
      if (this.currentOrder && this.currentOrder.status === 'PENDING') {
        try {
          const stored = localStorage.getItem('booking_orders_fake_db')
          if (stored) {
            const orders = JSON.parse(stored)
            const found = orders.find((o) => String(o.id) === String(this.currentOrder.id))
            if (found) {
              found.status = 'CANCELLED'
              localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
            }
          }
        } catch (e) {
          console.error(e)
        }
      }

      this.$alert('Đơn hàng của bạn đã hết thời gian giữ vé (15 phút) và bị hủy tự động. Các vé đã được giải phóng lại hệ thống.', 'Hết thời gian giữ vé', {
        confirmButtonText: 'Đồng ý',
        type: 'warning'
      }).then(() => {
        this.isCheckingOut = false
        this.currentOrder = null
        this.fetchCapacitiesAndOrders()
      })
    },

    handleCancelCheckout() {
      if (this.currentOrder && this.currentOrder.status === 'PENDING') {
        try {
          const stored = localStorage.getItem('booking_orders_fake_db')
          if (stored) {
            const orders = JSON.parse(stored)
            const found = orders.find((o) => String(o.id) === String(this.currentOrder.id))
            if (found) {
              found.status = 'CANCELLED'
              localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
            }
          }
        } catch (e) {
          console.error(e)
        }
      }

      this.stopCountdownTimer()
      this.isCheckingOut = false
      this.currentOrder = null
      this.fetchCapacitiesAndOrders()
    },

    handleNextToConfirm() {
      let allValid = true
      for (let i = 0; i < this.ticketsAnswers.length; i++) {
        const refName = `ticketForm_${i}`
        const formRef = this.$refs[refName]
        const refObj = Array.isArray(formRef) ? formRef[0] : formRef
        if (refObj) {
          refObj.validate((valid) => {
            if (!valid) {
              allValid = false
              this.activeTicketTab = String(i)
            }
          })
        }
      }

      if (allValid) {
        this.activeStep = 1
        this.savePendingOrderState()
      } else {
        this.$message.warning('Vui lòng điền đầy đủ thông tin bắt buộc cho tất cả các vé!')
      }
    },

    handleProceedToPayment() {
      this.activeStep = 2
      this.savePendingOrderState()
    },

    handleProcessPayment() {
      this.submittingPayment = true
      setTimeout(() => {
        this.stopCountdownTimer()

        // Update status to PAID
        try {
          const stored = localStorage.getItem('booking_orders_fake_db')
          if (stored) {
            const orders = JSON.parse(stored)
            const idx = orders.findIndex((o) => String(o.id) === String(this.currentOrder.id))
            if (idx !== -1) {
              orders[idx].status = 'PAID'
              orders[idx].payment_method = this.paymentMethod
              orders[idx].tickets = JSON.parse(JSON.stringify(this.ticketsAnswers))
              orders[idx].customer = { ...this.ticketsAnswers[0] }
              localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
              this.completedOrder = orders[idx]
              this.currentOrder = orders[idx]
            }
          }
        } catch (e) {
          console.error('Error completing payment:', e)
        }

        this.submittingPayment = false
        this.activeStep = 3
        this.fetchCapacitiesAndOrders()
        this.$message.success('Thanh toán thành công!')
      }, 1200)
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-detail-public-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

// Header
.booking-header {
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;

  .header-container {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand-logo {
    display: flex;
    align-items: center;
    cursor: pointer;

    .logo-icon {
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 20px;
      margin-right: 12px;
    }

    .brand-title {
      font-size: 20px;
      font-weight: 700;
      color: #1f2937;
      margin: 0;
      line-height: 1.2;
    }

    .brand-subtitle {
      font-size: 11px;
      color: #6b7280;
    }
  }

  .header-nav {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.place-detail-wrapper {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

// Capacity Status Card
.capacity-status-card {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 16px 20px;

  .cap-box {
    background: #ffffff;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .cap-label {
      font-size: 11px;
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
    }

    .cap-val {
      font-size: 16px;
      font-weight: 700;
      margin-top: 2px;
    }
  }
}

.countdown-badge {
  background: #ef4444;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 800;
  margin-left: 6px;
}

// Booking Section
.booking-checkout-card {
  border-radius: 16px;
  overflow: hidden;

  .card-header-flex {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    margin-bottom: 8px;

    .title {
      font-size: 22px;
    }
  }
}

.sub-title {
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 16px;
}

// Price Table Box
.price-table-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;

  .price-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
    color: #475569;
  }

  .price-divider {
    height: 1px;
    background: #cbd5e1;
    margin: 12px 0;
  }

  .total-unit-row {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;

    .unit-price {
      color: #0284c7;
      font-size: 18px;
    }
  }
}

// Quantity Box
.booking-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

  .form-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
  }

  .total-summary-box {
    background: #fef2f2;
    border: 1px dashed #fca5a5;
    border-radius: 8px;
    padding: 14px;
    text-align: center;

    .total-label {
      font-size: 12px;
      font-weight: 700;
      color: #991b1b;
    }

    .total-price {
      font-size: 24px;
      font-weight: 800;
      color: #dc2626;
      margin-top: 4px;
    }
  }

  .btn-continue {
    width: 100%;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 14px 20px;
  }
}

// Steps Content
.step-content {
  background: #ffffff;
  border-radius: 12px;

  .step-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 24px;
  }
}

// Summary Box
.summary-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;

  &.payment-summary-bg {
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .summary-box-title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 16px;
  }

  p {
    margin-bottom: 10px;
    font-size: 14px;
    color: #334155;
  }

  .summary-line {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 14px;

    &.divider {
      border-top: 1px solid #cbd5e1;
      margin: 12px 0;
    }

    &.total-line {
      font-size: 18px;
      font-weight: 700;

      .price-large {
        font-size: 22px;
      }
    }
  }
}

// Simulated Payment
.payment-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.simulated-gateway-box {
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  margin: 0 auto;

  .qr-placeholder {
    width: 180px;
    height: 180px;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .qr-icon {
      font-size: 90px;
      color: #334155;
    }
  }
}

// Success Step
.success-icon-wrap {
  .success-check-icon {
    font-size: 72px;
    color: #22c55e;
  }
}

.order-receipt-card {
  max-width: 500px;
  margin: 0 auto;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;

  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .receipt-body p {
    margin-bottom: 8px;
    font-size: 14px;
    color: #334155;
  }
}

.receipt-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.display-block {
  display: block;
}
.font-size-xs {
  font-size: 12px;
}
.font-size-md {
  font-size: 15px;
}
</style>
